/**
 * @author NikolaV
 *
 * Usage:
 * var splitter = new ui.Splitter($('#left'), $('#right'), opts?);
 */

import './Splitter.scss';

interface ISplitterOptions {
    minWidth?: number;
    maxWidth?: number;
    startWidth?: number;
    resetThreshold?: number;
    handleWidth?: number;
    closedHightlight?: boolean;
}

export class Splitter {
    static defaults: ISplitterOptions = {
        minWidth: 250,
        maxWidth: Math.max($(window).innerWidth() / 1.5, 400),
        startWidth: 350,
        resetThreshold: 3,
        handleWidth: 25,
        closedHightlight: true,
    };
    private readonly leftPane: HTMLElement;
    private readonly rightPane: HTMLElement = null;
    private readonly handle: HTMLElement;
    private readonly $handle: JQuery;
    private readonly options: ISplitterOptions;
    private draggedDistance = 0;
    private posOnElem = 0;
    private readonly defaultState: any;

    constructor(private readonly $leftPane: JQuery, private readonly $rightPane?: JQuery, opts?: ISplitterOptions) {
        if (!(this instanceof Splitter)) {
            return new Splitter($leftPane, $rightPane, opts);
        }
        this.leftPane = $leftPane[0] as HTMLElement;
        if (this.$rightPane) {
            this.rightPane = $rightPane[0] as HTMLElement;
        }
        this.options = $.extend({}, Splitter.defaults, opts);

        this.defaultState = {
            width: this.options.startWidth + 'px',
            left: 0,
        };
        this.$handle = $('<div id="resizeHandle" class="resizeHandle-style"></div>').prependTo(this.$leftPane);
        this.handle = this.$handle[0] as HTMLElement;
        this.initialize();
    }

    expand() {
        this.animateTo(this.defaultState, 200, () => {
            if (this.rightPane) {
                this.rightPane.style.marginLeft = this.options.startWidth + 1 + 'px';
            }
            this.setBg();
        });
        this.$handle.removeClass('visible-bg');
        this.$leftPane.removeClass('slide-out');
    }

    collapse() {
        this.animateTo(
            {
                width: this.options.minWidth + 'px',
                left: -this.options.minWidth + 'px',
            },
            200,
            () => {
                if (this.rightPane) {
                    this.rightPane.style.marginLeft = '1px';
                }
                this.setBg();
            }
        );
    }

    toggle(eventPageX = 0) {
        this.draggedDistance = this.draggedDistance - eventPageX;
        const left = parseInt(this.leftPane.style.left, 10);
        const width = parseInt(this.leftPane.style.width, 10);

        // hide
        if (
            (left < -(this.options.minWidth / 3) && left !== -this.options.minWidth) ||
            (Math.abs(this.draggedDistance) < this.options.resetThreshold && left === 0 && Math.abs(width - this.options.startWidth) < this.options.resetThreshold)
        ) {
            this.collapse();
        }

        // reset to default
        else if (Math.abs(this.draggedDistance) < this.options.resetThreshold) {
            this.expand();
        } else {
            this.setBg();
        }
    }

    private initialize() {
        this.leftPane.style.width = this.defaultState.width;
        this.leftPane.style.left = '0px';
        if (this.rightPane) {
            this.rightPane.style.marginLeft = this.defaultState.width;
        }
        // bind events
        this.$handle.on('mousedown.splitter', $.proxy(this.startDragHandler, this));
        this.$handle.on('mouseup.splitter', $.proxy(this.endDragHandler, this));
        $(document).on('mouseup.splitter', () => $(document).off('mousemove.splitter'));
    }

    private startDragHandler(e) {
        if (e.which === 1) {
            this.draggedDistance = e.pageX;
            this.posOnElem = e.pageX - this.$handle[0].getBoundingClientRect().left;
            $(document).on('mousemove.splitter', _.throttle(this.mouseMoveHandler, 10));
        }
        return false;
    }

    private endDragHandler(e) {
        if (e.which === 1) {
            this.toggle(e.pageX);
        }
    }

    private mouseMoveHandler = e => {
        e.preventDefault();
        e.stopPropagation();
        const { minWidth, maxWidth } = this.options;

        const newLeft = utils.clamp(-minWidth, -minWidth + e.pageX - this.posOnElem, 0);
        this.leftPane.style.width = utils.clamp(minWidth, e.pageX - this.posOnElem, maxWidth) + 'px';
        this.leftPane.style.left = newLeft + 'px';
        if (this.rightPane) {
            this.rightPane.style.marginLeft = utils.clamp(0, e.pageX - this.posOnElem, maxWidth) + 1 + 'px';
        }
        this.$leftPane.addRemoveClass(newLeft < -(minWidth / 3), 'slide-out');
    };

    private animateTo(state, speed = 200, callback?) {
        const animOptions: any = {
            duration: speed,
            complete: callback,
        };

        if (this.rightPane) {
            animOptions.step = () => {
                this.rightPane.style.marginLeft = this.handle.getBoundingClientRect().left + 1 + 'px';
            };
        }

        this.$leftPane.animate(state, animOptions).css('overflow', 'visible');
    }

    /** Add visible bg if collapsed to the left */
    private setBg() {
        if (this.options.closedHightlight) {
            const addBg = parseInt(this.leftPane.style.left, 10) < -this.options.minWidth + 20;
            this.$handle.addRemoveClass(addBg, 'visible-bg');
        }
    }
}
